/*
 * The one composer every surface renders, and the loader behind it (F-2 §6.1). Whether a person
 * writes in the formatted editor or in the markdown source view is their own setting
 * (`core.composer.source_mode`), read here and nowhere else in the tree: the surfaces know nothing
 * of each other and the consumers know nothing of the setting.
 *
 * The core — the draft, the tray, the send — lives here, above the surface, and a surface is a
 * view over it: the formatted editor is the composer, the source view is a view of it, and the two
 * are swapped under a person's hands without a second draft hook coming into being. That is what
 * makes every swap lossless by construction — the source view toggled mid-message, and the
 * stand-in below — rather than by the timing of a draft write against a subscription.
 *
 * The formatted editor is a chunk of its own, fetched on first use, and the fallback while it is
 * on its way is the real textarea rather than a spinner: it accepts text from the first frame and
 * writes it into the very draft the editor then opens over. `preloadEditor()` is called once at
 * boot, right after the modules are bound, so on every visit but the very first the chunk is
 * cached before a conversation mounts.
 *
 * A surface's own row under the tools (`belowTools`) is drawn by the loader, not by the surface: it
 * lives in a node of its own that the loader portals into and that moves into whichever frame is on
 * screen. So the row is mounted once for the composer's life — the AI chat's status bar keeps an open
 * popover, and the thread pane's tick, across the swap — while on screen it stands exactly where the
 * frame puts it, inside the box under the tools.
 */

import {
	lazy,
	Suspense,
	useCallback,
	useImperativeHandle,
	useRef,
	useState,
	type ReactNode,
} from "react"
import { createPortal } from "react-dom"
import { useComposerSettings } from "./settings.ts"
import { TextareaComposer } from "./TextareaComposer.tsx"
import type { ComposerHandle, ComposerProps } from "./types.ts"
import { useComposerCore, type StandIn } from "./useComposerCore.ts"

/**
 * How long the stand-in's caret note means "somebody is typing here". It covers the swap itself —
 * the chunk is fetched and evaluated in well under a second on a warm cache, and a slow line only
 * makes the stand-in live longer, not the note older — and nothing else.
 */
const STANDIN_NOTE_MS = 2_000

/** The editor's entry, and the only place in the application that names its chunk (F-2 §16). */
const editorChunk = () => import("../editor/index.ts")

const LazyEditorComposer = lazy(() => editorChunk().then((m) => ({ default: m.EditorComposer })))

export function Composer(props: ComposerProps) {
	const { ref } = props
	const { sourceMode } = useComposerSettings()

	// The surface's handle is read by two parties: the consumer that passed `ref`, and the core,
	// which gives the caret back through it once a send settled and cannot know which element that
	// is. The loader holds the surface's own handle and serves the consumer a forwarding one, which
	// exists from the first frame and follows a change of surface on its own.
	const surface = useRef<ComposerHandle | null>(null)
	useImperativeHandle(
		ref,
		() => ({
			focus: () => surface.current?.focus(),
			getValue: () => surface.current?.getValue() ?? "",
			setValue: (v, caret) => surface.current?.setValue(v, caret),
			insertAtCaret: (text) => surface.current?.insertAtCaret(text),
			caretInFencedBlock: () => surface.current?.caretInFencedBlock() ?? false,
		}),
		[],
	)
	const refocus = useCallback(() => surface.current?.focus(), [])
	const core = useComposerCore(props, { refocus })

	// What the stand-in knew and the editor cannot see: whether somebody was typing into it, and
	// where. The two surfaces never exist at once, so the note lives here, in the one thing that
	// outlives the swap — the stand-in writes it on every caret move, the editor restores it into the
	// box it opens. Reading it does not clear it: in development React builds the editor's view,
	// throws it away and builds it again, and a note the discarded view had eaten would leave the
	// second one unfocused — which is the whole thing this note exists to prevent. What bounds it is
	// its age, not its use.
	const caret = useRef<StandIn | null>(null)
	const noteCaret = useCallback((state: StandIn | null) => {
		caret.current = state
	}, [])
	const takeCaret = useCallback(() => {
		const note = caret.current
		if (note === null) return null
		if (Date.now() - note.at > STANDIN_NOTE_MS) {
			caret.current = null
			return null
		}
		return note
	}, [])

	const below = useBelowTools(props.belowTools)

	if (sourceMode) {
		return (
			<>
				<TextareaComposer {...props} ref={surface} core={core} belowTools={below.slot} />
				{below.row}
			</>
		)
	}
	return (
		<>
			<Suspense
				fallback={
					<TextareaComposer
						{...props}
						ref={surface}
						core={core}
						belowTools={below.slot}
						noteCaret={noteCaret}
						loading
					/>
				}
			>
				<LazyEditorComposer
					{...props}
					ref={surface}
					core={core}
					belowTools={below.slot}
					takeCaret={takeCaret}
				/>
			</Suspense>
			{below.row}
		</>
	)
}

/**
 * The row under the tools, drawn once for every surface the loader shows (the header's last
 * paragraph). `row` is the row itself, portalled into a node the loader owns; `slot` is what a frame
 * draws in its place, an empty host the node moves into as the frame mounts. Neither host takes part
 * in the layout (`display: contents`), so the frame's flex column sees the row's own element, as it
 * did when the surface drew it. A composer without the row draws neither.
 */
function useBelowTools(content: ReactNode): { slot: ReactNode; row: ReactNode } {
	const [node] = useState(() => {
		const element = document.createElement("div")
		element.className = "tb-composer__below"
		return element
	})
	// `appendChild` moves the node: whichever frame mounted last holds it, and a frame that goes away
	// takes nothing with it that the next one needs.
	const adopt = useCallback(
		(host: HTMLDivElement | null) => {
			host?.appendChild(node)
		},
		[node],
	)
	if (content === undefined || content === null || content === false) {
		return { slot: undefined, row: null }
	}
	return {
		slot: <div ref={adopt} className="tb-composer__below" />,
		row: createPortal(content, node),
	}
}

/** Fetches the editor's chunk ahead of the first composer that wants it. */
export function preloadEditor(): void {
	void editorChunk().catch(() => undefined)
}
